import { useId } from 'react';
import { Area, AreaChart, Line, LineChart, ResponsiveContainer, YAxis } from 'recharts';
import { useChartAnimation } from './use-chart-animation';

/**
 * Shape-only trend for a KPI tile: filled area plus line, no axes or labels.
 * The y-domain is the data's own min/max so small movements stay legible —
 * a sparkline answers "which way and how steadily", not "how much".
 */
export function Sparkline({
  data,
  color,
  height = 30,
}: {
  data: number[];
  color: string;
  height?: number;
}) {
  // useId keeps the gradient's id unique per instance — a value-derived id
  // collides whenever two tiles happen to end on the same number, and the
  // second chart then paints with the first one's colour.
  const gradientId = useId();
  const animation = useChartAnimation();
  const points = data.map((value, i) => ({ i, value }));

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <AreaChart data={points} margin={{ top: 3, right: 3, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.22} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <YAxis hide domain={['dataMin', 'dataMax']} />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={false}
            {...animation}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export interface TrendSeries {
  key: string;
  values: number[];
  color: string;
}

/**
 * Compact multi-series trend sharing one 0-based scale, so the series can be
 * compared by height rather than only by shape. Used under a tile's headline
 * figures to give the current month a 12-month context.
 */
export function TrendLines({
  labels,
  series,
  height = 54,
}: {
  labels: string[];
  series: TrendSeries[];
  height?: number;
}) {
  const animation = useChartAnimation();
  const data = labels.map((label, i) => {
    const row: Record<string, string | number> = { label };
    for (const s of series) row[s.key] = s.values[i] ?? 0;
    return row;
  });

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <YAxis hide domain={[0, 'dataMax']} />
          {series.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              {...animation}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
